import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import ScheduleModal from '../components/ScheduleModal.js';
import BatchScheduleModal from '../components/BatchScheduleModal.js';

const styles = {
  filtersContainer: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '5px',
    borderBottom: '1px solid #dee2e6',
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' },
  filterItem: { display: 'flex', flexDirection: 'column', gap: '5px' },
  filterLabel: { fontSize: '0.8rem', fontWeight: 'bold', color: '#555', paddingLeft: '2px' },
  select: { padding: '8px', fontSize: '1rem', width: '160px' },
  searchInput: { padding: '8px', fontSize: '1rem', width: '160px' },
  tableContainer: { marginTop: '20px', overflowX: 'auto' },
  table: { width: '100%', minWidth: '900px', borderCollapse: 'collapse' },
  th: { border: '1px solid #ddd', padding: '8px', background: '#f4f4f4', fontSize: '0.9rem', whiteSpace: 'nowrap', textAlign: 'center' },
  td: { border: '1px solid #ddd', padding: '8px', fontSize: '0.9rem', textAlign: 'center' },
  thCheckbox: { width: '40px', padding: '8px', background: '#f4f4f4', border: '1px solid #ddd', textAlign: 'center' },
  tdCheckbox: { width: '40px', padding: '8px', border: '1px solid #ddd', textAlign: 'center' },
  button: { padding: '8px 12px', cursor: 'pointer', border: 'none', borderRadius: '4px', fontSize: '1rem' },
  searchButton: { backgroundColor: '#17a2b8', color: 'white' },
  modalButton: { backgroundColor: '#17a2b8', color: 'white' },
  batchButton: { backgroundColor: '#007bff', color: 'white' },
  pdfButton: { backgroundColor: '#28a745', color: 'white' },
  fetchHoursButton: { backgroundColor: '#6f42c1', color: 'white' },
  countDisplay: { fontSize: '1rem', fontWeight: 'bold', color: '#333', marginTop: '15px', paddingLeft: '5px' },
};

const ScheduleManagePage = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [searchTerms, setSearchTerms] = useState({
    searchName: '',
    searchWorkplace: '',
    searchDistrict: '',
  });
  const [appliedSearch, setAppliedSearch] = useState({
    searchName: '',
    searchWorkplace: '',
    searchDistrict: '',
  });
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [hasSearched, setHasSearched] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState(null);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ sortBy: null, sortOrder: 'ASC' });
  const [fetchHoursModalOpen, setFetchHoursModalOpen] = useState(false);
  const [fetchHoursStep, setFetchHoursStep] = useState('confirm'); // 'confirm' | 'loading' | 'result'
  const [fetchHoursResult, setFetchHoursResult] = useState({ successCount: 0, noDataList: [] });

  const monthOptions = [];
  for (let m = 1; m <= 12; m++) monthOptions.push(m);

  const fetchProjects = useCallback(async () => {
    if (!selectedYear) return;
    try {
      const res = await api.get(`/projects?year=${selectedYear}`);
      setProjects(res.data);
      if (res.data.length > 0 && !selectedProject) setSelectedProject(res.data[0].id);
    } catch (err) {
      console.error('사업 목록 로딩 실패', err);
    }
  }, [selectedYear, selectedProject]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fetchScheduleList = useCallback(async () => {
    if (!selectedYear || !selectedProject || !selectedMonth) return;
    setLoading(true);
    setError('');
    setMessage('');
    setSelectedIds(new Set());
    try {
      const params = {
        year: selectedYear,
        projectId: selectedProject,
        month: selectedMonth,
        ...appliedSearch,
      };
      if (sortConfig.sortBy) {
        params.sortBy = sortConfig.sortBy;
        params.sortOrder = sortConfig.sortOrder;
      }
      const res = await api.get('/schedule', { params });
      setListData(res.data.list ?? res.data);
    } catch (err) {
      setError(err.response?.data?.message || '명단 조회 실패');
      setListData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedProject, selectedMonth, appliedSearch, sortConfig]);

  useEffect(() => {
    if (hasSearched) fetchScheduleList();
  }, [fetchScheduleList, hasSearched]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerms((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchClick = () => {
    setAppliedSearch(searchTerms);
    setHasSearched(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearchClick();
  };

  const handleSort = (columnName) => {
    setSortConfig((prev) => {
      let sortOrder = 'ASC';
      if (prev.sortBy === columnName && prev.sortOrder === 'ASC') {
        sortOrder = 'DESC';
      }
      return { sortBy: columnName, sortOrder };
    });
    if (!hasSearched) {
      // 아직 조회 전이면 기본 검색을 먼저 수행
      setAppliedSearch(searchTerms);
      setHasSearched(true);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const ids = listData.map((item) => item.participant.id);
      setSelectedIds(new Set(ids));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (participantId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(participantId)) next.delete(participantId);
      else next.add(participantId);
      return next;
    });
  };

  const openModal = (item) => {
    setModalTarget({
      participantId: item.participant.id,
      participantName: item.participant.name,
      year: selectedYear,
      month: selectedMonth,
      scheduleRecord: item.scheduleRecord,
      version: item.scheduleRecord?.version ?? 0,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalTarget(null);
  };

  const handleModalSaved = () => {
    setMessage('저장되었습니다.');
    fetchScheduleList();
    closeModal();
  };

  const openBatchModal = () => {
    if (selectedIds.size === 0) {
      alert('일괄 입력할 명단을 선택해 주세요.');
      return;
    }
    setBatchModalOpen(true);
  };

  const closeBatchModal = () => {
    setBatchModalOpen(false);
  };

  const handleBatchSaved = () => {
    setMessage(`${selectedIds.size}명의 스케줄이 일괄 저장되었습니다.`);
    fetchScheduleList();
    closeBatchModal();
  };

  const handlePdfDownload = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      alert('다운로드할 명단을 선택해 주세요.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.post(
        '/schedule/print',
        { participantIds: ids, year: selectedYear, month: selectedMonth },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `근무스케줄표_${selectedYear}년${String(selectedMonth).padStart(2, '0')}월.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage('PDF 다운로드가 완료되었습니다.');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'PDF 생성 실패';
      setError(msg);
      if (err.response?.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const j = JSON.parse(reader.result);
            setError(j.message || msg);
          } catch (_) {
            setError(msg);
          }
        };
        reader.readAsText(err.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const openFetchHoursModal = () => {
    setFetchHoursStep('confirm');
    setFetchHoursResult({ successCount: 0, noDataList: [] });
    setFetchHoursModalOpen(true);
  };

  const closeFetchHoursModal = () => {
    setFetchHoursModalOpen(false);
    setFetchHoursStep('confirm');
    setFetchHoursResult({ successCount: 0, noDataList: [] });
    fetchScheduleList();
  };

  const handleFetchPrevHours = async () => {
    setFetchHoursStep('loading');
    setError('');
    setMessage('');
    const participantIds = listData.map((item) => item.participant.id);
    try {
      const res = await api.post('/schedule/apply-prev-hours', {
        year: selectedYear,
        projectId: selectedProject,
        month: selectedMonth,
        participantIds,
      });
      setFetchHoursResult({
        successCount: res.data.successCount ?? 0,
        noDataList: res.data.noDataList ?? [],
      });
      setFetchHoursStep('result');
      setMessage('전달 근무시간 가져오기가 완료되었습니다.');
    } catch (err) {
      setError(err.response?.data?.message || '전달 근무시간 가져오기 실패');
      setFetchHoursModalOpen(false);
    }
  };

  const isAllSelected = listData.length > 0 && selectedIds.size === listData.length;

  const fetchHoursModalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
    },
    modal: {
      background: '#fff',
      borderRadius: '8px',
      padding: '24px',
      width: '90%',
      maxWidth: '480px',
      maxHeight: '85vh',
      overflow: 'auto',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    },
    title: { fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '16px' },
    body: { marginBottom: '20px', lineHeight: 1.6 },
    resultSection: { marginTop: '12px', marginBottom: '8px' },
    tipBox: {
      marginTop: '14px',
      padding: '10px 12px',
      background: '#f0f4ff',
      border: '1px solid #c5d0f0',
      borderRadius: '6px',
      fontSize: '0.9rem',
      color: '#333',
      lineHeight: 1.5,
    },
    noDataTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', marginTop: '8px' },
    noDataTh: { border: '1px solid #ddd', padding: '6px 10px', background: '#f4f4f4', textAlign: 'left' },
    noDataTd: { border: '1px solid #ddd', padding: '6px 10px' },
    buttonRow: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },
    cancelBtn: { ...styles.button, backgroundColor: '#6c757d', color: 'white' },
    primaryBtn: { ...styles.button, backgroundColor: '#6f42c1', color: 'white' },
  };

  return (
    <div>
      <h2>스케줄 표 관리</h2>

      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>연도</span>
            <input
              type="number"
              style={styles.select}
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10) || selectedYear)}
              min="2020"
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사업명</span>
            <select style={styles.select} value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
              <option value="">-- 사업명 선택 --</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.projectName}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>월</span>
            <select style={styles.select} value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
              {monthOptions.map((m) => (
                <option key={m} value={m}>{m}월</option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>성명</span>
            <input
              type="text"
              name="searchName"
              placeholder="성명 검색"
              value={searchTerms.searchName}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>근무장소</span>
            <input
              type="text"
              name="searchWorkplace"
              placeholder="근무장소 검색"
              value={searchTerms.searchWorkplace}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>읍면동</span>
            <input
              type="text"
              name="searchDistrict"
              placeholder="읍면동 검색"
              value={searchTerms.searchDistrict}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              style={styles.searchInput}
            />
          </div>
          <div style={{ ...styles.filterItem, marginLeft: 'auto', flexDirection: 'row', gap: '8px' }}>
            <button style={{ ...styles.button, ...styles.searchButton }} onClick={handleSearchClick} disabled={loading}>
              검색
            </button>
            <button
              style={{ ...styles.button, ...styles.batchButton }}
              onClick={openBatchModal}
              disabled={loading || selectedIds.size === 0}
            >
              선택 일괄 입력 ({selectedIds.size})
            </button>
            <button
              style={{ ...styles.button, ...styles.pdfButton }}
              onClick={handlePdfDownload}
              disabled={loading || selectedIds.size === 0}
            >
              PDF 다운로드 ({selectedIds.size})
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '15px', flexWrap: 'wrap' }}>
          <span style={styles.countDisplay}>
            {loading ? '검색 중...' : `조회된 인원: 총 ${listData.length} 명`}
          </span>
          <button
            type="button"
            style={{ ...styles.button, ...styles.fetchHoursButton }}
            onClick={openFetchHoursModal}
            disabled={loading || listData.length === 0}
          >
            근무시간 가져오기
          </button>
        </div>
      </div>

      {message && <p style={{ color: 'green', marginTop: '10px' }}>{message}</p>}
      {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}

      <div style={styles.tableContainer}>
        {loading ? (
          <p>로딩 중...</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.thCheckbox}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    disabled={listData.length === 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={styles.th} onClick={() => handleSort('name')}>성명 ↕</th>
                <th style={styles.th}>주민등록번호</th>
                <th style={styles.th}>담당자</th>
                <th style={styles.th} onClick={() => handleSort('workplace')}>근무장소 ↕</th>
                <th style={styles.th}>스케줄 지정</th>
              </tr>
            </thead>
            <tbody>
              {listData.length > 0 ? (
                listData.map((item) => {
                  const p = item.participant;
                  const isSelected = selectedIds.has(p.id);
                  return (
                    <tr key={p.id}>
                      <td style={styles.tdCheckbox}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectOne(p.id)}
                        />
                      </td>
                      <td style={styles.td}>{p.name}</td>
                      <td style={styles.td}>{p.rrn ? `${p.rrn.substring(0, 8)}...` : '-'}</td>
                      <td style={styles.td}>{p.registrar || '-'}</td>
                      <td style={styles.td}>{p.workplace || '-'}</td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          style={{ ...styles.button, ...styles.modalButton }}
                          onClick={() => openModal(item)}
                        >
                          스케줄 지정
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} style={styles.td}>
                    {error || '데이터가 없습니다. 연도, 사업, 월을 선택한 뒤 검색해 주세요.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <ScheduleModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSaved={handleModalSaved}
        participantId={modalTarget?.participantId}
        participantName={modalTarget?.participantName}
        year={modalTarget?.year}
        month={modalTarget?.month}
        initialWorkDays={modalTarget?.scheduleRecord?.workDays ?? []}
        initialRestDays={modalTarget?.scheduleRecord?.restDays ?? []}
        initialEducationDays={modalTarget?.scheduleRecord?.educationDays ?? []}
        initialDailyHours={modalTarget?.scheduleRecord?.dailyHours}
        initialStartTime={modalTarget?.scheduleRecord?.startTime ?? ''}
        initialEndTime={modalTarget?.scheduleRecord?.endTime ?? ''}
        initialVersion={modalTarget?.version ?? 0}
      />

      <BatchScheduleModal
        isOpen={batchModalOpen}
        onClose={closeBatchModal}
        onSaved={handleBatchSaved}
        participantIds={Array.from(selectedIds)}
        year={selectedYear}
        month={selectedMonth}
      />

      {fetchHoursModalOpen && (
        <div style={fetchHoursModalStyles.overlay} onClick={(e) => e.target === e.currentTarget && fetchHoursStep !== 'loading' && closeFetchHoursModal()}>
          <div style={fetchHoursModalStyles.modal} onClick={(e) => e.stopPropagation()}>
            {fetchHoursStep === 'confirm' && (
              <>
                <div style={fetchHoursModalStyles.title}>전달 근무시간 가져오기</div>
                <div style={fetchHoursModalStyles.body}>
                  전달에서 근무시간 정보(1일 소정근로시간, 시업시간, 종업시간)를 가져옵니다.
                  <br />
                  현재 조회된 {listData.length}명에게 적용됩니다. 진행하시겠습니까?
                  <div style={fetchHoursModalStyles.tipBox}>
                    💡 근무시간 가져오기 전에 <strong>선택 일괄 입력</strong>으로 근무일·휴무일 등을 먼저 정리한 뒤 가져오기를 하시면 업무에 더 편리합니다.
                  </div>
                </div>
                <div style={fetchHoursModalStyles.buttonRow}>
                  <button style={fetchHoursModalStyles.cancelBtn} onClick={closeFetchHoursModal}>
                    취소
                  </button>
                  <button style={fetchHoursModalStyles.primaryBtn} onClick={handleFetchPrevHours}>
                    가져오기
                  </button>
                </div>
              </>
            )}
            {fetchHoursStep === 'loading' && (
              <>
                <div style={fetchHoursModalStyles.title}>전달 근무시간 가져오기</div>
                <div style={fetchHoursModalStyles.body}>가져오는 중...</div>
              </>
            )}
            {fetchHoursStep === 'result' && (
              <>
                <div style={fetchHoursModalStyles.title}>가져오기 완료</div>
                <div style={fetchHoursModalStyles.body}>
                  <div style={fetchHoursModalStyles.resultSection}>
                    <strong>성공한 인원: {fetchHoursResult.successCount}명</strong>
                  </div>
                  {fetchHoursResult.noDataList && fetchHoursResult.noDataList.length > 0 ? (
                    <div style={fetchHoursModalStyles.resultSection}>
                      <strong>정보가 없어 못 가져온 명단 ({fetchHoursResult.noDataList.length}명):</strong>
                      <table style={fetchHoursModalStyles.noDataTable}>
                        <thead>
                          <tr>
                            <th style={fetchHoursModalStyles.noDataTh}>번호</th>
                            <th style={fetchHoursModalStyles.noDataTh}>성명</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fetchHoursResult.noDataList.map((row, idx) => (
                            <tr key={row.participantId}>
                              <td style={fetchHoursModalStyles.noDataTd}>{idx + 1}</td>
                              <td style={fetchHoursModalStyles.noDataTd}>{row.name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={fetchHoursModalStyles.resultSection}>
                      정보가 없어 못 가져온 인원은 없습니다.
                    </div>
                  )}
                </div>
                <div style={fetchHoursModalStyles.buttonRow}>
                  <button style={fetchHoursModalStyles.primaryBtn} onClick={closeFetchHoursModal}>
                    확인
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleManagePage;
